import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileStack, CheckCircle2, Trash2, Plus, Loader2, RefreshCw, BadgeCheck } from 'lucide-react';

export const DRAFT_BUILDERS = ['reroof', 'painting', 'guttering', 'maintenance', 'repairs'];
import { QuotationTypeSelect } from '@/components/QuotationTypeSelect';
import PricingDrawer from '@/components/PricingDrawer';


function QuoteBanner({ api, quote, builder, roofType, list, busy, setBusy, onSwitch, onNew, onConfirmed, onDeleted, onRefreshDoc }) {
  const isDraft = !!quote.is_draft;
  const run = async (fn, ok, fail) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof ok === 'function' ? ok(r) : ok); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || fail); return null; }
    finally { setBusy(false); }
  };
  const confirmDraft = async () => {
    if (!window.confirm('Confirm this draft as a real quote? Builder lines are pushed automatically and it gets its own appraisal number.')) return;
    const r = await run(() => api.post(`/appraisals/${quote.id}/confirm-draft`), (res) => `Quote confirmed as A${res.data.child.appraisal_no} — you can now Save Quote PDF`, 'Confirm failed');
    if (r) onConfirmed(r.data.child);
  };
  const deleteDraft = async () => {
    if (!window.confirm('Delete this draft and all of its builder lines? The parent appraisal is not affected.')) return;
    const r = await run(() => api.delete(`/appraisals/${quote.id}/draft`), 'Draft deleted', 'Delete failed');
    if (r) onDeleted();
  };
  const syncPins = async () => {
    const gutter = builder === 'guttering';
    if (!window.confirm(gutter
      ? "Re-copy from the appraisal? This draft's diagram pins, Gutter Details and each run's ends / profile / material are replaced with the appraisal's current values."
      : "Replace this draft's roof diagram pins with the parent's current pins?")) return;
    const r = await run(() => api.post(`/appraisals/${quote.id}/draft-sync-pins`), (res) => `Copied ${res.data.pins} pins${res.data.gutter_details_copied ? ' + Gutter Details' : ''} from the appraisal`, 'Copy failed');
    if (r) onRefreshDoc();
  };
  const btn = 'text-xs font-semibold px-3 py-1.5 rounded transition-colors flex items-center gap-1';
  const tone = isDraft ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-emerald-300 bg-emerald-50 text-emerald-900';
  return (
    <div className={`rounded-md border-2 px-4 py-3 space-y-2 ${tone}`} data-testid="draft-gate-banner">
      <div className="flex items-center gap-3 flex-wrap">
        {isDraft ? <FileStack className="w-5 h-5 text-sky-700 shrink-0" /> : <BadgeCheck className="w-5 h-5 text-emerald-700 shrink-0" />}
        <div className="text-sm flex-1 min-w-[240px]" data-testid="draft-gate-label">
          {isDraft
            ? <><b>{quote.variant_label}</b> · draft quote. Steps: pick Quotation Type → build → Confirm quote → Save Quote PDF.</>
            : <><b>A{quote.appraisal_no}</b> · confirmed quote{quote.variant_label ? ` (${quote.variant_label})` : ''}. Save Quote PDF pushes the latest builder values automatically.</>}
          {quote.variant_group_id && <span data-testid="draft-gate-variant-chip" className="ml-2 whitespace-nowrap text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">Option in a variant group{quote.variant_superseded ? ' · SUPERSEDED' : ''}</span>}
        </div>
        {list && list.length > 1 && (
          <select value={quote.id} onChange={(e) => onSwitch(e.target.value)} disabled={busy}
            className="text-sm border border-slate-300 rounded px-2 py-1 bg-white" data-testid="draft-gate-switch">
            {list.map(d => <option key={d.id} value={d.id}>{d.is_draft ? d.variant_label : `A${d.appraisal_no} · ${d.variant_label || 'confirmed'}${d.variant_superseded ? ' · superseded' : ''}`}</option>)}
          </select>
        )}
        {onNew && (
          <button onClick={onNew} disabled={busy} data-testid="draft-gate-new-btn"
            className={`${btn} border border-sky-400 text-sky-800 bg-white hover:bg-sky-100`}>
            <Plus className="w-3.5 h-3.5" /> New draft
          </button>
        )}
        {isDraft && (
          <>
            <button onClick={syncPins} disabled={busy} data-testid="draft-gate-sync-pins-btn"
              className={`${btn} border border-sky-400 text-sky-800 bg-white hover:bg-sky-100`}>
              <RefreshCw className="w-3.5 h-3.5" /> {builder === 'guttering' ? 'Re-copy from appraisal' : 'Re-copy diagram'}
            </button>
            <button onClick={confirmDraft} disabled={busy || !quote.quotation_type} data-testid="draft-gate-confirm-btn"
              title={!quote.quotation_type ? 'Pick a Quotation Type first' : undefined}
              className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Confirm quote
            </button>
            <button onClick={deleteDraft} disabled={busy} data-testid="draft-gate-delete-btn"
              className={`${btn} border border-rose-300 text-rose-700 bg-white hover:bg-rose-50`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete draft
            </button>
          </>
        )}
      </div>
      <QuotationTypeSelect api={api} quoteId={quote.id} builder={builder} roofType={roofType}
        current={quote.quotation_type} onSaved={onRefreshDoc} />
    </div>
  );
}

// Stage 2 · Runs a builder against its own quote child so the parent's components stay untouched.
export function DraftBuilderGate({ api, appraisal, builder, onChanged, children }) {
  const selfId = appraisal?._id || appraisal?.id;
  const isChild = !!appraisal?.parent_appraisal_id;
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [doc, setDoc] = useState(null);
  const [roofType, setRoofType] = useState('');
  const [busy, setBusy] = useState(false);
  const [copyTick, setCopyTick] = useState(0);
  const inflight = useRef(false);

  const loadDoc = useCallback(async (id) => {
    const { data } = await api.get(`/appraisals/${id}`);
    setDoc(data);
    setCurrent(c => (c && c.id === id ? { ...c, quotation_type: data.quotation_type || '', is_draft: !!data.is_draft, appraisal_no: data.appraisal_no } : c));
  }, [api]);

  const refreshList = useCallback(async () => {
    const { data } = await api.get(`/appraisals/${selfId}/builder-drafts`, { params: { builder } });
    const l = [...(data?.confirmed || []), ...(data?.drafts || [])];
    setList(l);
    return l;
  }, [api, selfId, builder]);

  const open = useCallback(async (makeNew = false) => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/appraisals/${selfId}/builder-draft`, { builder, new: makeNew });
      setRoofType(data.roof_type || '');
      if (!isChild) await refreshList();
      setCurrent(data.draft);
      await loadDoc(data.draft.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open draft');
    } finally { setBusy(false); inflight.current = false; }
  }, [api, selfId, builder, isChild, refreshList, loadDoc]);

  useEffect(() => { if (selfId) open(false); }, [selfId, open]);
  useEffect(() => {
    const onVariant = async (e) => {
      const id = e.detail?.id;
      if (!id) return;
      if (isChild) { window.location.href = `/appraisals/${id}?tab=Builder`; return; }
      const l = await refreshList();
      setCurrent(l.find(x => x.id === id) || { id, is_draft: true });
      await loadDoc(id);
    };
    window.addEventListener('rtr:variant-quote-created', onVariant);
    return () => window.removeEventListener('rtr:variant-quote-created', onVariant);
  }, [isChild, refreshList, loadDoc]);

  if (isChild && !appraisal.is_draft && !appraisal.builder_origin) return (
    <div className="space-y-3">
      <div className="flex justify-end"><PricingDrawer api={api} appraisalId={selfId} /></div>
      {children(selfId, appraisal, null)}
    </div>
  );
  if (!current || !doc) {
    return <div className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="draft-gate-loading"><Loader2 className="w-4 h-4 animate-spin" /> Opening quote…</div>;
  }
  const switchTo = async (id) => { setCurrent(list.find(x => x.id === id)); await loadDoc(id); };
  const onConfirmed = async (child) => {
    if (isChild) { onChanged && onChanged(); }
    else { await refreshList(); }
    setCurrent({ ...child, is_draft: false });
    await loadDoc(child.id);
  };
  const onDeleted = async () => {
    if (isChild) { if (window.opener) window.close(); else window.history.back(); return; }
    await open(false);
  };
  return (
    <div className="space-y-3" data-testid={`draft-gate-${builder}`}>
      <QuoteBanner api={api} quote={current} builder={builder} roofType={roofType} list={isChild ? null : list}
        busy={busy} setBusy={setBusy} onSwitch={switchTo} onNew={isChild ? null : () => open(true)}
        onConfirmed={onConfirmed} onDeleted={onDeleted} onRefreshDoc={() => { loadDoc(current.id); setCopyTick(t => t + 1); }} />
      <div className="flex justify-end"><PricingDrawer key={current.id} api={api} appraisalId={current.id} quoteLabel={current.is_draft ? `Draft ${current.variant_label || ''}`.trim() : `A${current.appraisal_no}`} /></div>
      <div key={`${current.id}-${current.is_draft ? 'd' : 'c'}-${doc?.roof_diagram_annotations_updated_at || ''}`}
        className={current.is_draft ? 'rtr-draft-mode' : 'rtr-quote-mode'}>
        <React.Fragment key={copyTick}>{children(current.id, doc, () => loadDoc(current.id))}</React.Fragment>
      </div>
    </div>
  );
}
